/**
 * ShareSheet — Menu de partage custom 3 options (Apps natif / Email / Download).
 *
 * Pattern : host monté globalement dans App.js (<ShareSheetHost />).
 * Pour ouvrir : `showShareSheet({ imageUrl, title?, message? })`.
 *
 * Utilité : sur web Outlook / Gmail / autres apps mail ne savent pas extraire
 * une image d'un share URL → on offre un menu explicite :
 *   • Apps   → Web Share API niveau 2 (avec fichier si supporté, sinon URL)
 *   • Email  → mailto: avec sujet et URL de l'image en pièce-jointe (URL)
 *   • Download → télécharge le fichier sur l'appareil (PC: file save; mobile: gallery)
 */

import React, { useEffect, useState, useCallback } from 'react';
import {
  View, Text, TouchableOpacity, StyleSheet, Platform, Modal, Pressable,
  DeviceEventEmitter, ActivityIndicator, Alert, Share,
} from 'react-native';
import { getCurrentLang } from '../i18n';

// Portal sur web pour stacker au-dessus de TOUS les modals (lightbox, etc.)
let createPortal = null;
if (Platform.OS === 'web') {
  try { createPortal = require('react-dom').createPortal; } catch {}
}
// FIX v1.12.744 — Ne PAS utiliser useI18n() dans ce host car il est monté
// hors de <I18nProvider> dans App.js (comme les autres hosts).
// On utilise getCurrentLang() + labels hardcodés FR/EN à la place.

function getLabels() {
  const lang = (() => { try { return getCurrentLang() || 'fr'; } catch { return 'fr'; } })();
  if (lang === 'en') {
    return {
      share: 'Share',
      subtitle: 'Choose a method',
      apps: 'Apps',
      apps_desc: 'WhatsApp, Messages, Insta…',
      email: 'Email',
      email_desc: 'Open mail client',
      download: 'Download',
      download_desc: 'Save image',
      processing: 'Processing…',
      cancel: 'Cancel',
    };
  }
  return {
    share: 'Partager',
    subtitle: 'Choisissez une méthode',
    apps: 'Apps',
    apps_desc: 'WhatsApp, Messages, Insta…',
    email: 'Email',
    email_desc: 'Ouvrir client mail',
    download: 'Télécharger',
    download_desc: "Sauver l'image",
    processing: 'En cours…',
    cancel: 'Annuler',
  };
}

const GOLD = '#D4AF72';
const DARK = '#0E0C0A';

const EVENT_SHOW = '__share_sheet_show__';
const EVENT_HIDE = '__share_sheet_hide__';

export function showShareSheet({ imageUrl, title, message }) {
  if (!imageUrl) return;
  DeviceEventEmitter.emit(EVENT_SHOW, { imageUrl, title, message });
}

export function hideShareSheet() {
  DeviceEventEmitter.emit(EVENT_HIDE);
}

function getDefaultTexts() {
  const lang = (() => { try { return getCurrentLang() || 'fr'; } catch { return 'fr'; } })();
  if (lang === 'en') {
    return {
      title: 'My MyTenue look',
      message: 'Check out my outfit composed by MyTenue — Your AI stylist ✨\nhttps://www.mytenue.com',
    };
  }
  return {
    title: 'Ma tenue MyTenue',
    message: 'Découvrez ma tenue composée par MyTenue — Votre styliste IA ✨\nhttps://www.mytenue.com',
  };
}

// ─── Action helpers ──────────────────────────────────────────────
async function shareViaApps({ imageUrl, title, message }) {
  if (Platform.OS === 'web' && typeof navigator !== 'undefined' && navigator.share) {
    // Web Share API niveau 2 (avec fichier si supporté)
    if (navigator.canShare) {
      try {
        const res = await fetch(imageUrl, { mode: 'cors' });
        if (res.ok) {
          const blob = await res.blob();
          const file = new File([blob], 'mytenue-look.png', { type: blob.type || 'image/png' });
          if (navigator.canShare({ files: [file] })) {
            await navigator.share({ title, text: message, files: [file] });
            return;
          }
        }
      } catch {}
    }
    // Fallback : URL + texte
    try { await navigator.share({ title, text: message, url: imageUrl }); }
    catch (e) { if (e?.name !== 'AbortError') throw e; }
    return;
  }
  // Mobile natif
  await Share.share({
    message: `${message}\n${imageUrl}`,
    url: Platform.OS === 'ios' ? imageUrl : undefined,
    title,
  });
}

function shareViaEmail({ imageUrl, title, message }) {
  // mailto: avec body = message + URL de l'image
  const subject = encodeURIComponent(title);
  const body = encodeURIComponent(`${message}\n\n${imageUrl}\n\n`);
  const mailto = `mailto:?subject=${subject}&body=${body}`;
  if (Platform.OS === 'web' && typeof window !== 'undefined') {
    window.location.href = mailto;
  } else {
    // RN : Linking
    const { Linking } = require('react-native');
    Linking.openURL(mailto).catch(() => {});
  }
}

async function downloadImage({ imageUrl }) {
  if (Platform.OS === 'web' && typeof document !== 'undefined') {
    try {
      const res = await fetch(imageUrl, { mode: 'cors' });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `mytenue-look-${Date.now()}.png`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (e) {
      // Fallback : ouvrir dans nouvel onglet, l'user fait right-click save
      if (typeof window !== 'undefined') window.open(imageUrl, '_blank');
    }
    return;
  }
  // Mobile : sauvegarder dans la galerie via MediaLibrary
  try {
    const MediaLibrary = await import('expo-media-library').catch(() => null);
    const FileSystem = await import('expo-file-system').catch(() => null);
    if (!MediaLibrary?.default && !MediaLibrary?.requestPermissionsAsync) {
      // expo-media-library pas installé → fallback Share
      await Share.share({ url: imageUrl, message: imageUrl });
      return;
    }
    const ML = MediaLibrary.default || MediaLibrary;
    const FS = FileSystem.default || FileSystem;
    const perm = await ML.requestPermissionsAsync();
    if (!perm.granted) {
      Alert.alert('Permission requise', 'Autorisez l\'accès à la galerie pour enregistrer.');
      return;
    }
    const fileUri = (FS.cacheDirectory || FS.documentDirectory) + `mytenue-${Date.now()}.png`;
    const dl = await FS.downloadAsync(imageUrl, fileUri);
    await ML.saveToLibraryAsync(dl.uri);
    Alert.alert('Enregistré', 'Image ajoutée à votre galerie.');
  } catch (e) {
    // Fallback : Share natif
    try { await Share.share({ url: imageUrl, message: imageUrl }); } catch {}
  }
}

// ─── Host component (à monter dans App.js, HORS de I18nProvider) ──────
export function ShareSheetHost() {
  const [state, setState] = useState(null); // { imageUrl, title, message } | null
  const [busy, setBusy] = useState(false);
  // Re-compute labels on each state change (assez pour suivre la lang)
  const labels = getLabels();

  useEffect(() => {
    const subShow = DeviceEventEmitter.addListener(EVENT_SHOW, (data) => {
      setState({ ...getDefaultTexts(), ...(data || {}) });
      setBusy(false);
    });
    const subHide = DeviceEventEmitter.addListener(EVENT_HIDE, () => setState(null));
    return () => { subShow.remove(); subHide.remove(); };
  }, []);

  const close = useCallback(() => { if (!busy) setState(null); }, [busy]);

  const handle = useCallback(async (action) => {
    if (!state || busy) return;
    setBusy(true);
    try {
      if (action === 'apps')     await shareViaApps(state);
      if (action === 'email')    await shareViaEmail(state);
      if (action === 'download') await downloadImage(state);
    } catch (e) {
      console.warn('[ShareSheet]', action, 'failed:', e?.message);
    } finally {
      setBusy(false);
      if (action === 'download') setState(null);
    }
  }, [state, busy]);

  if (!state) return null;

  const content = (
    <View style={s.bg}>
      <Pressable style={s.bgPressable} onPress={close} />
      <View style={s.sheet} pointerEvents="box-none">
        <View style={s.handle} />
        <Text style={s.title}>{labels.share}</Text>
        <Text style={s.subtitle}>{labels.subtitle}</Text>

        <View style={s.options}>
          <TouchableOpacity style={s.option} onPress={() => handle('apps')} disabled={busy} activeOpacity={0.8}>
            <View style={[s.optionIcon, { backgroundColor: '#E8F4FF' }]}>
              <Text style={s.optionIconText}>📱</Text>
            </View>
            <Text style={s.optionLabel}>{labels.apps}</Text>
            <Text style={s.optionDesc}>{labels.apps_desc}</Text>
          </TouchableOpacity>

          <TouchableOpacity style={s.option} onPress={() => handle('email')} disabled={busy} activeOpacity={0.8}>
            <View style={[s.optionIcon, { backgroundColor: '#FFF0E8' }]}>
              <Text style={s.optionIconText}>✉️</Text>
            </View>
            <Text style={s.optionLabel}>{labels.email}</Text>
            <Text style={s.optionDesc}>{labels.email_desc}</Text>
          </TouchableOpacity>

          <TouchableOpacity style={s.option} onPress={() => handle('download')} disabled={busy} activeOpacity={0.8}>
            <View style={[s.optionIcon, { backgroundColor: '#F2E8FF' }]}>
              <Text style={s.optionIconText}>⬇️</Text>
            </View>
            <Text style={s.optionLabel}>{labels.download}</Text>
            <Text style={s.optionDesc}>{labels.download_desc}</Text>
          </TouchableOpacity>
        </View>

        {busy ? (
          <View style={s.busyRow}>
            <ActivityIndicator size="small" color={GOLD} />
            <Text style={s.busyText}>{labels.processing}</Text>
          </View>
        ) : (
          <TouchableOpacity style={s.cancel} onPress={close} activeOpacity={0.7}>
            <Text style={s.cancelText}>{labels.cancel}</Text>
          </TouchableOpacity>
        )}
      </View>
    </View>
  );

  // ─── WEB : portal sur document.body pour passer AU-DESSUS de tout autre Modal ───
  if (Platform.OS === 'web') {
    if (createPortal && typeof document !== 'undefined' && document.body) {
      return createPortal(content, document.body);
    }
    return content;
  }

  // ─── MOBILE : Modal natif (gère bien le stacking sur iOS/Android) ───
  return (
    <Modal visible transparent animationType="slide" onRequestClose={close}>
      {content}
    </Modal>
  );
}

const s = StyleSheet.create({
  bg: {
    // v1.12.745 — Sur web : position fixed (rendu via portal sur body) au-dessus de tout
    ...Platform.select({
      web: {
        position: 'fixed',
        top: 0, left: 0, right: 0, bottom: 0,
        zIndex: 2147483647,
        backgroundColor: 'rgba(0,0,0,0.55)',
        justifyContent: 'flex-end',
        alignItems: 'center',
      },
      default: {
        flex: 1,
        backgroundColor: 'rgba(0,0,0,0.55)',
        justifyContent: 'flex-end',
      },
    }),
  },
  bgPressable: {
    position: 'absolute',
    top: 0, left: 0, right: 0, bottom: 0,
  },
  sheet: {
    backgroundColor: '#FFFFFF',
    borderTopLeftRadius: 20,
    borderTopRightRadius: 20,
    paddingTop: 10,
    paddingBottom: Platform.OS === 'ios' ? 40 : 24,
    paddingHorizontal: 20,
    ...Platform.select({
      web: { maxWidth: 520, width: '100%', borderRadius: 20, marginBottom: 24, zIndex: 2 },
      default: {},
    }),
  },
  handle: {
    width: 40, height: 4, borderRadius: 2, backgroundColor: '#E0DBD3',
    alignSelf: 'center', marginBottom: 12,
  },
  title: { fontSize: 18, fontWeight: '700', color: DARK, textAlign: 'center' },
  subtitle: { fontSize: 12, color: '#7A7268', textAlign: 'center', marginTop: 4, marginBottom: 20 },
  options: { flexDirection: 'row', justifyContent: 'space-around', gap: 10, marginBottom: 16 },
  option: {
    flex: 1, alignItems: 'center', padding: 14, borderRadius: 14,
    backgroundColor: '#FAFAFA', borderWidth: 1, borderColor: '#EAE6E0',
  },
  optionIcon: {
    width: 50, height: 50, borderRadius: 25,
    alignItems: 'center', justifyContent: 'center', marginBottom: 8,
  },
  optionIconText: { fontSize: 24 },
  optionLabel: { fontSize: 13, fontWeight: '600', color: DARK, marginBottom: 2 },
  optionDesc: { fontSize: 9, color: '#9B9489', textAlign: 'center', lineHeight: 12 },
  busyRow: { flexDirection: 'row', alignItems: 'center', justifyContent: 'center', gap: 10, paddingVertical: 14 },
  busyText: { fontSize: 13, color: '#5F584F' },
  cancel: {
    paddingVertical: 14, alignItems: 'center',
    borderRadius: 12, backgroundColor: '#F5F1EA', marginTop: 6,
  },
  cancelText: { fontSize: 14, fontWeight: '600', color: '#5F584F' },
});

export default ShareSheetHost;
